/**
 * Client-side media inspection before an upload: pixel size of images and videos and a small thumbnail (WebP, JPEG
 * fallback; longest edge THUMB_MAX_EDGE, ≤ MAX_THUMB_BYTES) for the message grid, video posters and download-only
 * image types (SVG, HEIC on non-Safari browsers). Browser APIs only (createImageBitmap / <video> + canvas).
 */
import { Files } from "@bundle/core";

export type MediaInfo = { width?: number; height?: number; thumb?: Blob };

const TIMEOUT_MS = 8000;

function withTimeout<T>(p: Promise<T>, ms = TIMEOUT_MS): Promise<T> {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(new Error("timeout")), ms);
    p.then(
      (v) => {
        clearTimeout(t);
        resolve(v);
      },
      (e: unknown) => {
        clearTimeout(t);
        reject(e instanceof Error ? e : new Error(String(e)));
      },
    );
  });
}

type Drawable = { source: CanvasImageSource; width: number; height: number; close?: () => void };

async function decodeImage(blob: Blob): Promise<Drawable> {
  try {
    const bmp = await createImageBitmap(blob, { imageOrientation: "from-image" });
    return { source: bmp, width: bmp.width, height: bmp.height, close: () => bmp.close() };
  } catch {
    // SVG and a few formats only decode through <img>.
    const url = URL.createObjectURL(blob);
    try {
      const img = new Image();
      img.decoding = "async";
      img.src = url;
      await img.decode();
      const width = img.naturalWidth || 512;
      const height = img.naturalHeight || 512;
      return { source: img, width, height, close: () => URL.revokeObjectURL(url) };
    } catch (e) {
      URL.revokeObjectURL(url);
      throw e;
    }
  }
}

async function videoFrame(blob: Blob): Promise<Drawable> {
  const url = URL.createObjectURL(blob);
  const video = document.createElement("video");
  video.muted = true;
  video.playsInline = true;
  video.preload = "auto";
  video.src = url;
  const once = (event: string) => new Promise<void>((resolve, reject) => {
    video.addEventListener(event, () => resolve(), { once: true });
    video.addEventListener("error", () => reject(new Error("video decode failed")), { once: true });
  });
  try {
    await once("loadeddata");
    const at = Number.isFinite(video.duration) && video.duration > 0 ? Math.min(0.5, video.duration / 2) : 0;
    if (at > 0) {
      video.currentTime = at;
      await once("seeked");
    }
    return { source: video, width: video.videoWidth, height: video.videoHeight, close: () => URL.revokeObjectURL(url) };
  } catch (e) {
    URL.revokeObjectURL(url);
    throw e;
  }
}

function toBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

async function encode(d: Drawable): Promise<Blob | undefined> {
  const scale = Math.min(1, Files.THUMB_MAX_EDGE / Math.max(d.width, d.height));
  const w = Math.max(1, Math.round(d.width * scale));
  const h = Math.max(1, Math.round(d.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (ctx === null) return undefined;
  ctx.drawImage(d.source, 0, 0, w, h);
  for (const quality of [0.82, 0.7, 0.55]) {
    let out = await toBlob(canvas, "image/webp", quality);
    // Browsers without WebP encoding hand back PNG: use JPEG instead.
    if (out === null || out.type !== "image/webp") out = await toBlob(canvas, "image/jpeg", quality);
    if (out !== null && out.size <= Files.MAX_THUMB_BYTES) return out;
  }
  return undefined;
}

/** Pixel size + thumbnail for images and videos; `{}` for anything else or when the browser can't decode it. */
export async function inspectMedia(blob: Blob, mime: string, name: string): Promise<MediaInfo> {
  const kind = Files.fileKind(mime, name);
  if (kind !== "image" && kind !== "video") return {};
  let d: Drawable | undefined;
  try {
    d = await withTimeout(kind === "image" ? decodeImage(blob) : videoFrame(blob));
    const info: MediaInfo = { width: d.width, height: d.height };
    if (d.width > 0 && d.height > 0) {
      const thumb = await withTimeout(encode(d));
      if (thumb !== undefined) info.thumb = thumb;
    }
    return info;
  } catch {
    return {};
  } finally {
    d?.close?.();
  }
}
